import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Loader2, AlertCircle } from "lucide-react";
import APSLogo from "../../../components/ui/APS Logo.svg";
import { getCurrentPolicies } from "../services/landingService";

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch {
    return null;
  }
};

const PrivacyPolicyPage = () => {
  const [policy, setPolicy]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0 });
    getCurrentPolicies()
      .then((res) => {
        const p = res?.data?.policies?.[0] ?? null;
        if (p) setPolicy(p);
        else setError("No active policy available");
      })
      .catch(() => setError("Unable to load the privacy policy. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  const publishedAt = formatDate(policy?.published_at ?? policy?.updated_at);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav bar */}
      <header className="border-b border-surface-border px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={APSLogo} alt="APS Logo" className="h-11 w-auto object-contain" />
        </Link>
        <Link to="/" className="text-sm text-brand-muted hover:text-primary transition-colors">
          &larr; Back to home
        </Link>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-7 h-7 animate-spin text-[#5B4FCF]" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <AlertCircle className="w-10 h-10 text-red-400" />
            <p className="text-sm text-brand-muted">{error}</p>
            <Link to="/" className="text-sm text-primary hover:underline">&larr; Back to home</Link>
          </div>
        ) : (
          <>
            {/* Title — hide when API returned a raw HTML document */}
            {!policy._rawHtml && (
              <div className="mb-10">
                <h1 className="text-3xl font-extrabold text-brand-text mb-3">
                  {policy.title || "Privacy Policy"}
                </h1>
                {publishedAt && (
                  <p className="text-sm text-brand-muted">
                    <span className="font-medium">Last Updated:</span> {publishedAt}
                  </p>
                )}
              </div>
            )}

            {/* Rendered HTML from API */}
            {policy._rawHtml ? (
              <iframe
                title="Privacy Policy"
                srcDoc={DOMPurify.sanitize(policy.content || "", { WHOLE_DOCUMENT: true, FORCE_BODY: false })}
                className="w-full border-0"
                style={{ minHeight: "80vh" }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div
                className="prose prose-sm max-w-none text-brand-muted leading-relaxed
                  prose-headings:text-brand-text prose-headings:font-bold
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-brand-text prose-strong:font-semibold
                  prose-ul:list-disc prose-ul:pl-5
                  prose-li:my-0.5"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(policy.content || ""),
                }}
              />
            )}

            {/* Footer note */}
            <div className="mt-12 pt-8 border-t border-surface-border text-center">
              <p className="text-xs text-brand-light">&copy; 2026 AIPolicyScan. All rights reserved.</p>
              <div className="flex justify-center gap-5 mt-3">
                <Link to="/terms" className="text-xs text-brand-muted hover:text-primary transition-colors">
                  Terms of Service
                </Link>
                <Link to="/" className="text-xs text-brand-muted hover:text-primary transition-colors">
                  Back to Home
                </Link>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
